/**
 * Building a reply or a forward out of a message already in the thread.
 *
 * Kept apart from the composer because these are the rules a mail client is
 * judged on -- who ends up on the Cc line, whose words appear under the quote
 * marker -- and they are much easier to reason about as functions over a
 * message than as branches inside a component that is also managing drafts,
 * uploads and presence.
 *
 * Everything here produces HTML that goes out as email, and everything here is
 * sanitised again server-side on write (see the reply route). Nothing in this
 * file is a security boundary; the escaping is so a contact called
 * `O'Rourke <ops>` renders as their name rather than as broken markup.
 */

import type { EmailAddress, Message } from "../types";
import { absoluteTime } from "./time";

/** What a message's menu asked the composer to do with it. */
export type IntentKind = "reply" | "reply-all" | "forward";

export interface ComposerIntent {
  kind: IntentKind;
  message: Message;
  /**
   * Bumped per request.
   *
   * Choosing Reply twice on the same message is a real thing an agent does --
   * they typed over the quote, changed their mind, and want it back. Without
   * something that differs, the second choice is an identical prop and the
   * composer never re-seeds.
   */
  nonce: number;
}

/**
 * The address a message came from, as a recipient.
 *
 * Null for anything with no From at all, which is what an internal note is --
 * and why the menu does not offer these actions on one.
 */
export function senderOf(message: Message): EmailAddress | null {
  if (!message.fromEmail) return null;
  return { name: message.fromName ?? undefined, email: message.fromEmail };
}

/**
 * Everyone else who was on the message, for Reply all.
 *
 * `ours` is every address the desk sends from. They are dropped because a desk
 * that Ccs itself creates a second copy of the reply arriving as new inbound
 * mail, which either raises a ticket about our own answer or appends it to this
 * one -- both of which have to be tidied up by hand.
 *
 * The sender is dropped too: they are the To line, and appearing in both is how
 * somebody gets two copies.
 */
export function replyAllCc(
  message: Message,
  ours: readonly string[],
): EmailAddress[] {
  const skip = new Set(ours.map((address) => address.trim().toLowerCase()));
  if (message.fromEmail) skip.add(message.fromEmail.trim().toLowerCase());

  const out: EmailAddress[] = [];

  for (const entry of [
    ...(message.toRecipients ?? []),
    ...(message.ccRecipients ?? []),
  ]) {
    const key = entry.email.trim().toLowerCase();
    if (!key || skip.has(key)) continue;
    skip.add(key);
    out.push(entry);
  }

  return out;
}

/**
 * An empty line to write on, above the message being answered.
 *
 * The blank paragraph first is not decoration: a contenteditable seeded with a
 * blockquote alone puts the caret inside the quote, so the reply gets typed
 * into the customer's own words and goes out looking like their message was
 * edited.
 */
export function quotedReply(message: Message): string {
  return `<p><br></p><p>${escapeText(attribution(message))}</p><blockquote>${bodyOf(
    message,
  )}</blockquote>`;
}

/**
 * The message, wrapped as a forward.
 *
 * A header block rather than a quote, and the headers are the point: whoever
 * receives this needs to know who wrote it, when, and who else already has it,
 * none of which survives pasting a body on its own.
 */
export function forwardBody(message: Message): string {
  const lines: string[] = [
    `From: ${describe(senderOf(message))}`,
    `Date: ${absoluteTime(message.sentAt)}`,
  ];

  if (message.subject) lines.push(`Subject: ${message.subject}`);
  if (message.toRecipients?.length) {
    lines.push(`To: ${message.toRecipients.map(describe).join(", ")}`);
  }
  if (message.ccRecipients?.length) {
    lines.push(`Cc: ${message.ccRecipients.map(describe).join(", ")}`);
  }

  const header = lines
    .map((line) => `<div>${escapeText(line)}</div>`)
    .join("");

  return [
    "<p><br></p>",
    "<p>---------- Forwarded message ----------</p>",
    header,
    "<p><br></p>",
    bodyOf(message),
  ].join("");
}

/** "Fwd: " once, however many times a subject has already been round. */
export function forwardSubject(subject: string): string {
  const trimmed = subject.trim();
  return /^fwd?:/i.test(trimmed) ? trimmed : `Fwd: ${trimmed}`;
}

/**
 * "Re: " once, however many times a subject has already been round.
 *
 * Mirrors `replySubject` in the API's send.service.ts, which is what actually
 * goes on the wire when the composer sends nothing. Duplicated deliberately:
 * this one exists so the subject field can show the agent what the header will
 * say before they change it, and a field that guessed differently from the
 * sender would be worse than no field.
 */
export function replySubject(subject: string): string {
  const trimmed = subject.trim();
  return /^re:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}

/** "On <date>, <someone> wrote:" -- the line every mail client puts here. */
function attribution(message: Message): string {
  const who =
    message.fromName ?? message.fromEmail ?? message.authorName ?? "somebody";
  return `On ${absoluteTime(message.sentAt)}, ${who} wrote:`;
}

/**
 * The message's content, HTML for preference.
 *
 * Both halves of it. The API hands the conversation view a message with its
 * quoted thread cut off, so that the thread is drawn once rather than once per
 * reply -- but that is a decision about a screen an agent is reading, not
 * about the mail we are about to send. Quoting only the new words would answer
 * the customer with a conversation shorter than the one in their own client,
 * and each round trip would prune it again.
 *
 * Falls back to the plain-text body wrapped in a `<pre>`: mail that arrived as
 * text is often a log or a stack trace, and reflowing it into a paragraph
 * destroys the alignment that made it readable.
 */
function bodyOf(message: Message): string {
  if (message.bodyHtml) return message.bodyHtml + (message.quotedHtml ?? "");
  if (message.bodyText) {
    return `<pre style="white-space:pre-wrap;font-family:inherit">${escapeText(
      message.bodyText,
    )}</pre>`;
  }
  return "";
}

/** `Name <address>`, or the bare address when there is no name. */
function describe(entry: EmailAddress | null): string {
  if (!entry) return "unknown";
  return entry.name ? `${entry.name} <${entry.email}>` : entry.email;
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
